import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, firstValueFrom } from 'rxjs';

export interface AppConfig {
  apiUrl: string;
  production: boolean;
  appName: string;
  version: string;
}

@Injectable({
  providedIn: 'root'
})
export class ConfigService {
  private config: AppConfig | null = null;

  constructor(private http: HttpClient) {}

  /**
   * Load configuration from config.json
   * This should be called before the app starts (via APP_INITIALIZER)
   */
  async loadConfig(): Promise<void> {
    try {
      // Add timestamp to prevent caching
      const timestamp = new Date().getTime();
      const configPath = `assets/config/config.json?v=${timestamp}`;

      this.config = await firstValueFrom(
        this.http.get<AppConfig>(configPath)
      );

      console.log('✅ Configuration loaded successfully');
      console.log('📍 API URL:', this.config.apiUrl);
      console.log('🏭 Production:', this.config.production);
    } catch (error) {
      console.error('❌ Error loading configuration:', error);
      // Fallback to default config
      this.config = {
        apiUrl: 'https://localhost:7181/api',
        production: false,
        appName: 'CMS UI',
        version: '1.0.0'
      };
      console.warn('⚠️ Using fallback configuration');
      console.warn('📍 Fallback API URL:', this.config.apiUrl);
    }
  }

  /**
   * Get the base API URL
   */
  getApiUrl(): string {
    if (!this.config) {
      console.error('❌ CRITICAL: Config not loaded yet! Services are trying to use API before initialization!');
      console.trace('Call stack:');
      // Return a fallback but this shouldn't happen if APP_INITIALIZER works
      return 'https://localhost:7181/api';
    }
    console.log('🔗 Returning API URL:', this.config.apiUrl);
    return this.config.apiUrl;
  }

  /**
   * Get the full configuration
   */
  getConfig(): AppConfig | null {
    return this.config;
  }

  /**
   * Check if running in production mode
   */
  isProduction(): boolean {
    return this.config?.production ?? false;
  }

  /**
   * Get app name
   */
  getAppName(): string {
    return this.config?.appName ?? 'CMS UI';
  }

  /**
   * Get app version
   */
  getVersion(): string {
    return this.config?.version ?? '1.0.0';
  }
}
